// src/pages/Returns/NewReturnInvoice.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, onSnapshot, query, writeBatch, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const styles = {
    container: { fontFamily: '"Inter", sans-serif', maxWidth: '1000px', margin: '30px auto', color: '#1f2937' },
    header: { marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    title: { fontSize: '28px', fontWeight: '800', color: '#111827', margin: 0, letterSpacing: '-0.5px' },
    subtitle: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
    backButton: { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' },
    box: { background: 'white', padding: '32px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', marginBottom: '24px' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none', height: '46px', backgroundColor: '#f9fafb' },
    select: { padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none', backgroundColor: '#f9fafb', height: '46px' },
    sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' },
    sectionTitle: { fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 },
    productInputGrid: { display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: '16px', alignItems: 'end', background: '#fef2f2', padding: '20px', borderRadius: '12px', border: '1px solid #fecaca', marginBottom: '24px' },
    button: { padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', height: '46px' },
    addButton: { background: '#ef4444', color: 'white' },
    saveButton: { background: '#ef4444', color: 'white', fontSize: '16px', padding: '14px 40px' },
    tableContainer: { border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', marginBottom: '32px' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { background: '#fef2f2', padding: '14px 16px', textAlign: 'left', borderBottom: '1px solid #fecaca', fontSize: '12px', fontWeight: '600', color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.5px' },
    td: { padding: '16px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#1f2937' },
    totalsWrapper: { display: 'flex', justifyContent: 'flex-end', marginTop: '24px' },
    totalsBox: { width: '320px', padding: '24px', background: '#fef2f2', borderRadius: '12px', border: '1px solid #fecaca' },
    totalRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#991b1b' },
    grandTotalRow: { display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '2px solid #fecaca' },
    grandTotalLabel: { fontSize: '16px', fontWeight: '700', color: '#991b1b' },
    grandTotalValue: { fontSize: '20px', fontWeight: '800', color: '#dc2626' },
    deleteBtn: { background: 'white', border: '1px solid #fee2e2', color: '#ef4444', cursor: 'pointer', padding: '8px', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    emptyState: { padding: '48px', textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', background: '#fafafa', borderRadius: '8px' },
    returnBadge: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fef2f2', color: '#dc2626', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: '1px solid #fecaca' }
};

function NewReturnInvoice() {
    const { currentUser, userData, canViewPrices } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [lineItems, setLineItems] = useState([]);
    const [vatRatePercent, setVatRatePercent] = useState(20);
    const [returnReason, setReturnReason] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);

    const [currentItem, setCurrentItem] = useState({ productId: '', quantity: 1 });

    const companyId = userData?.companyId || currentUser?.uid;

    useEffect(() => {
        if (!companyId) return;
        const unsubC = onSnapshot(query(collection(db, 'companies', companyId, 'customers')), snap => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubP = onSnapshot(query(collection(db, 'companies', companyId, 'products')), snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        return () => { unsubC(); unsubP(); };
    }, [companyId]);

    // Pre-populate from return data if navigated from Returns page
    useEffect(() => {
        if (!location.state?.returnData || customers.length === 0 || products.length === 0) return;
        const rd = location.state.returnData;

        if (rd.customerId) setSelectedCustomer(rd.customerId);

        // Support single return or multiple returns
        const returns = Array.isArray(rd.returns) ? rd.returns : [rd];
        const items = returns.map(r => {
            const product = products.find(p => p.id === r.productId);
            const price = r.unitPrice || product?.salePrice || 0;
            return {
                productId: r.productId,
                name: r.productName || product?.name || 'Bilinmeyen Ürün',
                quantity: Number(r.quantity) || 1,
                price: Number(price),
                total: Number(r.quantity || 1) * Number(price),
                returnId: r.returnId || r.id
            };
        });
        setLineItems(items);
        if (rd.reason) setReturnReason(rd.reason);
    }, [location.state, customers, products]);

    const handleAddItem = () => {
        if (!currentItem.productId || currentItem.quantity <= 0) return;
        const product = products.find(p => p.id === currentItem.productId);
        if (!product) return;
        setLineItems(prev => [...prev, {
            productId: product.id,
            name: product.name,
            quantity: Number(currentItem.quantity),
            price: Number(product.salePrice),
            total: Number(currentItem.quantity) * Number(product.salePrice)
        }]);
        setCurrentItem({ productId: '', quantity: 1 });
    };

    const handleRemoveItem = (index) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const subTotal = lineItems.reduce((acc, item) => acc + item.total, 0);
    const vatRate = vatRatePercent / 100;
    const vatAmount = subTotal * vatRate;
    const grandTotal = subTotal + vatAmount;

    const handleSave = async () => {
        if (!selectedCustomer || lineItems.length === 0) {
            toast.error('Lütfen müşteri seçin ve en az bir ürün ekleyin.');
            return;
        }
        setSaving(true);

        try {
            const customer = customers.find(c => c.id === selectedCustomer);
            const batch = writeBatch(db);

            // 1. Create return invoice in invoices collection
            const invoiceRef = doc(collection(db, 'companies', companyId, 'invoices'));
            const invoiceData = {
                type: 'return',
                customerId: customer.id,
                customerName: customer.name,
                invoiceDate: new Date(invoiceDate),
                lineItems: lineItems.map(item => ({
                    productId: item.productId,
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price,
                    total: item.total
                })),
                vatRate: vatRate,
                subTotal: subTotal,
                vatAmount: vatAmount,
                total: grandTotal,
                currency: 'TRY',
                returnReason: returnReason,
                description: description,
                returnIds: lineItems.filter(i => i.returnId).map(i => i.returnId),
                status: 'Oluşturuldu',
                createdAt: serverTimestamp()
            };
            batch.set(invoiceRef, invoiceData);

            // 2. Update customer balance (increase = reduce debt)
            const customerRef = doc(db, 'companies', companyId, 'customers', customer.id);
            const customerDoc = await getDoc(customerRef);
            if (customerDoc.exists()) {
                const customerData = customerDoc.data();
                const currentBalance = customerData.balance || 0;
                let balanceImpact = grandTotal;

                if (customerData.currency === 'USD') {
                    try {
                        const res = await fetch('https://api.frankfurter.app/latest?from=USD&to=TRY');
                        const data = await res.json();
                        const usdRate = data.rates.TRY || 33;
                        balanceImpact = grandTotal / usdRate;
                    } catch { balanceImpact = grandTotal / 33; }
                }

                const newBalance = currentBalance + balanceImpact;
                batch.update(customerRef, { balance: newBalance });

                const balanceHistoryRef = doc(collection(customerRef, 'balanceHistory'));
                batch.set(balanceHistoryRef, {
                    date: serverTimestamp(),
                    type: 'İade Faturası',
                    description: `İade faturası oluşturuldu - ${lineItems.length} kalem`,
                    amount: balanceImpact,
                    previousBalance: currentBalance,
                    newBalance: newBalance,
                    invoiceId: invoiceRef.id
                });
            }

            // 3. Update return records with invoice reference
            for (const item of lineItems) {
                if (item.returnId) {
                    const returnRef = doc(db, 'companies', companyId, 'returns', item.returnId);
                    batch.update(returnRef, {
                        returnInvoiceId: invoiceRef.id,
                        returnInvoiceStatus: 'created'
                    });
                }
            }

            // 4. Add transaction record
            const transRef = doc(collection(db, 'companies', companyId, 'transactions'));
            batch.set(transRef, {
                customerId: customer.id,
                customerName: customer.name,
                relatedPartyId: customer.id,
                type: 'İade Faturası',
                amount: grandTotal,
                currency: 'TRY',
                description: `İade Faturası: ${lineItems.map(i => i.name).join(', ')}`,
                date: serverTimestamp(),
                relatedDocumentId: invoiceRef.id,
                invoiceId: invoiceRef.id,
                status: 'completed'
            });

            await batch.commit();
            toast.success('İade faturası başarıyla oluşturuldu!');
            navigate('/iadeler');
        } catch (error) {
            console.error('İade faturası kaydedilirken hata:', error);
            toast.error('İade faturası kaydedilirken hata oluştu: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <h1 style={styles.title}>İade Faturası Oluştur</h1>
                        <span style={styles.returnBadge}>↩ İADE</span>
                    </div>
                    <p style={styles.subtitle}>İade edilen ürünler için iade faturası kesin.</p>
                </div>
                <button onClick={() => navigate('/iadeler')} style={styles.backButton}>← İadelere Dön</button>
            </div>

            <div style={styles.box}>
                <div style={styles.grid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Müşteri</label>
                        <select style={styles.select} value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)} required>
                            <option value="">Müşteri Seçin...</option>
                            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Fatura Tarihi</label>
                        <input style={styles.input} type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>KDV Oranı (%)</label>
                        <input type="number" style={styles.input} value={vatRatePercent}
                            onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v) && v >= 0 && v <= 100) setVatRatePercent(v); else if (e.target.value === '') setVatRatePercent(''); }}
                            placeholder="Örn: 20" />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>İade Nedeni</label>
                        <select style={styles.select} value={returnReason} onChange={e => setReturnReason(e.target.value)}>
                            <option value="">Neden Seçin...</option>
                            <option value="hatali_urun">Hatalı Ürün</option>
                            <option value="kargo_hasari">Kargo Hasarı</option>
                            <option value="degistirme">Değiştirme</option>
                            <option value="istenmeyen_urun">İstenmeyen Ürün</option>
                            <option value="diger">Diğer</option>
                        </select>
                    </div>
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>Açıklama (Opsiyonel)</label>
                    <textarea style={{ ...styles.input, height: 'auto', minHeight: '60px', resize: 'vertical' }}
                        value={description} onChange={e => setDescription(e.target.value)}
                        placeholder="İade faturası ile ilgili notlar..." />
                </div>

                <div style={{ ...styles.sectionHeader, marginTop: '32px' }}>
                    <h3 style={styles.sectionTitle}>İade Kalemleri</h3>
                </div>

                <div style={styles.productInputGrid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ürün</label>
                        <select style={styles.select} value={currentItem.productId} onChange={e => setCurrentItem(prev => ({ ...prev, productId: e.target.value }))}>
                            <option value="">Ürün Seçin...</option>
                            {products.map(p => <option key={p.id} value={p.id}>{p.name} - {canViewPrices ? `${p.salePrice} ₺` : '**'}</option>)}
                        </select>
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar</label>
                        <input style={styles.input} type="number" min="1" value={currentItem.quantity} onChange={e => setCurrentItem(prev => ({ ...prev, quantity: e.target.value }))} />
                    </div>
                    <div />
                    <button onClick={handleAddItem} style={{ ...styles.button, ...styles.addButton }}>
                        <span style={{ fontSize: '18px' }}>+</span> Ekle
                    </button>
                </div>

                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Ürün Adı</th>
                                <th style={styles.th} width="120">Miktar</th>
                                <th style={styles.th} width="150" align="right">Birim Fiyat</th>
                                <th style={styles.th} width="150" align="right">Toplam</th>
                                <th style={styles.th} width="60"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.length === 0 ? (
                                <tr><td colSpan="5" style={styles.emptyState}>Henüz iade kalemi eklenmedi.</td></tr>
                            ) : (
                                lineItems.map((item, index) => (
                                    <tr key={index}>
                                        <td style={styles.td}><div style={{ fontWeight: '500' }}>{item.name}</div></td>
                                        <td style={styles.td}>{item.quantity}</td>
                                        <td style={styles.td} align="right">{canViewPrices ? `${item.price.toFixed(2)} ₺` : '***'}</td>
                                        <td style={{ ...styles.td, fontWeight: '600', color: '#dc2626' }} align="right">{canViewPrices ? `${item.total.toFixed(2)} ₺` : '***'}</td>
                                        <td style={styles.td} align="center">
                                            <button onClick={() => handleRemoveItem(index)} style={styles.deleteBtn} title="Sil">🗑️</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={styles.totalsWrapper}>
                    <div style={styles.totalsBox}>
                        <div style={styles.totalRow}>
                            <span>Ara Toplam:</span>
                            <span>{canViewPrices ? `${subTotal.toFixed(2)} ₺` : '***'}</span>
                        </div>
                        <div style={styles.totalRow}>
                            <span>KDV (%{vatRatePercent || 0}):</span>
                            <span>{canViewPrices ? `${vatAmount.toFixed(2)} ₺` : '***'}</span>
                        </div>
                        <div style={styles.grandTotalRow}>
                            <span style={styles.grandTotalLabel}>İADE TOPLAMI:</span>
                            <span style={styles.grandTotalValue}>{canViewPrices ? `${grandTotal.toFixed(2)} ₺` : '***'}</span>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                    <button onClick={handleSave} disabled={saving} style={{ ...styles.button, ...styles.saveButton, opacity: saving ? 0.6 : 1 }}>
                        {saving ? '⏳ Kaydediliyor...' : '↩ İade Faturasını Kaydet'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default NewReturnInvoice;
